import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HeroButton } from '@/components/ui/button-variants';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Camera, Upload, X, MapPin, Hash } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '@/contexts/TranslationContext';

interface CreatePostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPetId?: string;
  onSuccess?: () => void;
}

interface Pet {
  id: string;
  name: string;
  species: string;
  avatar_url: string | null;
}

const CreatePostModal = ({ open, onOpenChange, selectedPetId, onSuccess }: CreatePostModalProps) => {
  const { user, profile } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pets, setPets] = useState<Pet[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    content: '',
    pet_id: '',
    location: '',
  });

  useEffect(() => {
    if (open && user) {
      fetchUserPets();
      if (selectedPetId) {
        setFormData(prev => ({ ...prev, pet_id: selectedPetId }));
      }
    }
  }, [open, user, selectedPetId]);

  const fetchUserPets = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('pets')
        .select('id, name, species, avatar_url')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPets(data || []);
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File troppo grande",
          description: "L'immagine deve essere inferiore a 5MB.",
          variant: "destructive",
        });
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;

    // Get current session to ensure authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const fileExt = imageFile.name.split('.').pop();
    const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('post-images')
      .upload(fileName, imageFile);

    if (uploadError) {
      console.error('Error uploading image:', uploadError);
      return null;
    }

    const { data } = supabase.storage
      .from('post-images')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Errore di autenticazione",
        description: "Devi essere autenticato per creare un post.",
        variant: "destructive",
      });
      return;
    }

    // Require either content or image
    if (!formData.content.trim() && !imageFile) {
      toast({
        title: "Contenuto richiesto",
        description: "Aggiungi del testo o un'immagine al tuo post.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Verify authentication before proceeding
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Sessione scaduta",
          description: "Effettua nuovamente l'accesso.",
          variant: "destructive",
        });
        return;
      }

      const imageUrl = await uploadImage();

      const { error } = await supabase
        .from('posts')
        .insert({
          user_id: session.user.id,
          content: formData.content.trim() || null,
          image_url: imageUrl,
          pet_id: formData.pet_id || null,
          location: formData.location.trim() || null,
          is_public: true,
        });

      if (error) throw error;

      toast({
        title: "Post pubblicato!",
        description: "Il tuo post è stato pubblicato con successo.",
      });

      // Reset form
      setFormData({
        content: '',
        pet_id: '',
        location: '',
      });
      setImageFile(null);
      setImagePreview(null);
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: "Errore",
        description: "Non è stato possibile pubblicare il post.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getSpeciesEmoji = (species: string) => {
    switch (species.toLowerCase()) {
      case 'cane': return '🐕';
      case 'gatto': return '🐱';
      case 'coniglio': return '🐰';
      case 'uccello': return '🐦';
      case 'pesce': return '🐠';
      case 'tartaruga': return '🐢';
      case 'criceto': return '🐹';
      case 'furetto': return '🦫';
      default: return '🐾';
    }
  };

  const selectedPet = pets.find(pet => pet.id === formData.pet_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/20 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-center">Crea un Nuovo Post</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Info */}
          <div className="flex items-center space-x-3 pb-4 border-b border-white/10">
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white font-bold">
                {profile?.display_name?.charAt(0).toUpperCase() || profile?.username?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-white">
                {profile?.display_name || profile?.username || 'Utente'}
              </p>
              {selectedPet && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">con</span>
                  <Badge variant="secondary" className="text-xs">
                    {getSpeciesEmoji(selectedPet.species)} {selectedPet.name}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="glass border-white/20 text-white resize-none min-h-[120px]"
              placeholder={t('profile.postPlaceholder')}
              maxLength={2000}
            />
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span>{formData.content.length}/2000</span>
              <div className="flex items-center gap-1">
                <Hash className="w-3 h-3" />
                <span>Usa #hashtag per maggiore visibilità</span>
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            {imagePreview ? (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-64 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label 
                htmlFor="image-upload"
                className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/30 transition-colors"
              >
                <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">Aggiungi un'immagine</span>
                <span className="text-xs text-muted-foreground">PNG, JPG fino a 5MB</span>
              </label>
            )}
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>

          {/* Pet Selection */}
          {pets.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm text-white">Tagga un pet (opzionale)</label>
              <Select value={formData.pet_id} onValueChange={(value) => setFormData({ ...formData, pet_id: value })}>
                <SelectTrigger className="glass border-white/20 text-white">
                  <SelectValue placeholder={t('profile.selectPet')} />
                </SelectTrigger>
                <SelectContent className="glass border-white/20">
                  {pets.map((pet) => (
                    <SelectItem key={pet.id} value={pet.id} className="text-white">
                      <div className="flex items-center gap-2">
                        {getSpeciesEmoji(pet.species)} {pet.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Location */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm text-white">Posizione (opzionale)</label>
            </div>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 glass border border-white/20 rounded-md text-white placeholder:text-muted-foreground"
              placeholder={t('profile.addLocation')}
              maxLength={100}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                onOpenChange(false);
                // Reset form when closing
                setFormData({ content: '', pet_id: '', location: '' });
                setImageFile(null);
                setImagePreview(null);
              }}
              className="flex-1"
              disabled={loading}
            >
              Annulla
            </Button>
            <HeroButton 
              type="submit" 
              className="flex-1" 
              disabled={(!formData.content.trim() && !imageFile) || loading}
            >
              {loading ? "Pubblicazione..." : "Pubblica"}
            </HeroButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePostModal;